import React from 'react';

import Form from 'mobx-react-form';
import { toNumber } from 'lodash';
import { IconButton, Stack, TableCell, TableRow, TextField, Tooltip } from '@mui/material';
import Cancel from '@mui/icons-material/Cancel';

import NumericInput from 'components/shared/inputs/NumericInput';
import { Adjustment } from 'types/underwriting/income-approach/cap-rate-conclusion';

export const getInputStyles = (alignRight = false) => ({
  fontSize: '14px',
  '& .MuiOutlinedInput-root': {
    fontSize: 'inherit',
    margin: 0,
    height: 'inherit',
    '& fieldset': {
      display: 'none',
    },
    '& input': {
      fontSize: 'inherit',
      padding: 0,
      paddingLeft: 1,

      ...(alignRight && {
        textAlign: 'right',
      }),
    },
  },
});

type IncomeAdjustmentRowProps = {
  form: Form;
  fieldPath: string;
  adjustment: Adjustment;
  onRemove: () => void;
};

const IncomeAdjustmentRow = ({ form, fieldPath, adjustment, onRemove }: IncomeAdjustmentRowProps) => {
  const [isHovering, setIsHovering] = React.useState(false);

  const onMouseEnter = React.useCallback(() => {
    setIsHovering(true);
  }, []);
  const onMouseLeave = React.useCallback(() => {
    setIsHovering(false);
  }, []);

  const handleChange = (data: Partial<Adjustment>) => {
    try {
      const targetField = form.$(fieldPath);
      targetField.set({
        ...targetField.value,
        ...data,
      });
    } catch (error) {
      console.error('Error setting field', error);
    }
  };

  return (
    <TableRow onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <TableCell>
        <TextField
          sx={{ ...getInputStyles() }}
          fullWidth
          value={adjustment.name}
          onChange={(e) => handleChange({ name: e.target.value })}
          placeholder={adjustment.name || 'Income Adjustment'}
        />
      </TableCell>
      <TableCell>&nbsp;</TableCell>
      <TableCell align="right">
        <NumericInput
          sx={{ ...getInputStyles(true) }}
          value={adjustment.amount}
          onChange={(value) => handleChange({ amount: toNumber(value) })}
          onFocus={(event) => event.target?.select()}
          allowNegative={true}
          decimalScale={2}
          fullWidth={false}
          placeholder="0.00"
          prefix="$"
          thousandSeparator
        />
      </TableCell>
      <TableCell />
      <TableCell>
        {isHovering && (
          <Stack alignItems="center" direction="row" justifyContent="end" sx={{ my: -1 }}>
            <Tooltip title="Remove Adjustment" placement="top">
              <IconButton onClick={onRemove} size="small">
                <Cancel color="error" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
};

export default IncomeAdjustmentRow;
